'use client';

import { CaretUpDown } from '@phosphor-icons/react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '@utils/cn';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { __config } from '../config';
import { ShortcutKbd } from './Shortcut';
import { Wobble, WobbleProps } from './wobble';

export const buttonVariantsClasses = cva(
  cn(
    'inline-flex shrink-0 items-center justify-center',
    'text-sm font-medium whitespace-nowrap',
    'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    // Add border to make transitions between variants smoother... because border takes 1 pixel of space in each direction
    'border-t border-l border-r',
    'border-b-[4px]',
    'active:border-b-[1px] hover:active:border-b-[1px]',
    'shadow-sm active:shadow-none',
    'transition-all',
  ),
  {
    variants: {
      variant: {
        /* ------------------------ Solid ----------------------- */
        default: 'border-border/80 bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-border/80 bg-destructive text-destructive-foreground',
        /* ----------------------- Outline ---------------------- */
        outline: 'bg-background hover:text-accent-foreground',
        'destructive-outline': 'border-destructive text-destructive',
        'ai-outline':
          'border-purple-600 text-purple-600 bg-background [&_kbd]:text-purple-600 [&_kbd]:border-purple-600',
        'info-outline':
          'border-sky-600 text-sky-600 bg-background [&_kbd]:text-sky-600 [&_kbd]:border-sky-600',
        /* --------------------- No Outline --------------------- */
        ghost:
          'border-transparent border-b text-muted-foreground hover:text-secondary-foreground shadow-none',
        link: 'border-transparent underline-offset-4 hover:underline shadow-none',
      },
      size: {
        xs: 'py-1 rounded-sm px-2',
        sm: 'h-9 rounded-md px-2',
        md: 'h-10 rounded-md px-3',
        lg: 'h-10 rounded-md px-4',
        icon: 'w-fit h-fit p-2',
        'icon-sm': 'w-fit h-fit p-1',
        fluid: 'w-full h-10',
        fit: 'w-fit h-fit p-1.5',
        selfless: 'w-fit h-fit !p-0',
      },
      rounded: {
        full: 'rounded-full',
        lg: 'rounded-lg',
        md: 'rounded-md',
        sm: 'rounded-sm',
        none: 'rounded-none',
        xl: 'rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      rounded: 'xl',
    },
  },
);
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariantsClasses> {
  asChild?: boolean;
  loading?: boolean;
  wobble?: boolean;
  wobbleProps?: Partial<WobbleProps>;
  shortcut?: string;
  dropdown?: boolean;
  invalid?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      type = 'button',
      loading,
      wobble,
      wobbleProps,
      shortcut,
      dropdown = false,
      disabled,
      invalid = false,
      children,
      ...props
    },
    ref,
  ) => {
    const isWobbleEnabled: boolean = wobble ?? __config.wobble.button;
    const Comp = asChild ? Slot : 'button';

    return (
      <Wobble ref={ref} off={!isWobbleEnabled} {...wobbleProps}>
        <Comp
          className={cn(
            buttonVariantsClasses({
              variant: invalid ? 'destructive-outline' : variant,
              size,
              rounded,
              className,
            }),
          )}
          disabled={loading || disabled}
          ref={ref}
          type={type}
          {...props}
        >
          {/* TODO implement StatefulContent loading */}
          <Slottable>{children}</Slottable>
          {shortcut && <ShortcutKbd className="ml-2">{shortcut}</ShortcutKbd>}
          {dropdown && <CaretUpDown className="ml-2 h-4 w-4 opacity-50" />}
        </Comp>
      </Wobble>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariantsClasses as buttonVariants };
